import React, { useCallback, useState, useEffect, useRef } from 'react';
import './Header.scss';
import { FocusTrap } from 'focus-trap-react';
import logo from '../../assets/img/charter-logo-v2.svg';
import { DesktopMainMenu } from './MainMenu/DesktopMainMenu';
import { PolicyHeader } from './PolicyHeader/PolicyHeader';
import { SearchInput } from './SearchInput/SearchInput';
import { SearchResults } from '../SearchResults/SearchResults';
import { SearchTrigger } from './SearchTrigger/SearchTrigger';
import { SidebarTrigger } from './SidebarTrigger/SidebarTrigger';
import { SitesMenu } from './SitesMenu/SitesMenu';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { AnalyticsController } from '../Analytics';
import {
    initAutoAnalytics,
    cleanupAutoAnalytics,
} from '../../shared/analytics/AutoParseAnalytics';
import { KeyboardKey } from '../../shared/enums/keyboardKeys.enum';
import { CompanyBanner } from '../../components/CompanyBanner/CompanyBanner';

interface Props {
    sidebarOpen: boolean;
    setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    searchOpen: boolean;
    setSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
    pathname: string;
    onBlur?: () => void;
    searchInline?: boolean;
}

export const Header = ({
    sidebarOpen,
    setSidebarOpen,
    searchOpen,
    setSearchOpen,
    pathname,
    onBlur,
    searchInline,
}: Props) => {
    const [showChild, setShowChild] = useState(false);
    const [sitesNavExpanded, setSitesNavExpanded] = useState(false);
    const { headerRef, isPolicy, sitesNavRef, hideNav } = useLayoutContext();
    const [headerHeight, setHeaderHeight] = useState(0);
    const shopSpectrumServiceButtonRef = useRef(null);

    useEffect(() => {
        setShowChild(true);
    }, []);
    
    useEffect(() => {
        const rumEnv = isPolicy
            ? process.env.GATSBY_DD_RUM_ENV_GA
            : process.env.GATSBY_DD_RUM_ENV_CEW;
        
        if (!rumEnv?.startsWith(`prod`)) return;

        import('@datadog/browser-rum').then(({ datadogRum }) => {
            if (isPolicy) {
                datadogRum.init({
                    applicationId: `14d82929-8f0a-4370-8d58-da41ee64182b`,
                    clientToken: `pub4df8205dbd6cdbaeaecc4af1def199fc`,
                    site: `datadoghq.com`,
                    service: `cew-ga`,
                    env: `${process.env.GATSBY_DD_RUM_ENV_GA}`,
                    sessionSampleRate: 70,
                    sessionReplaySampleRate: 20,
                    trackUserInteractions: true,
                    trackResources: true,
                    trackLongTasks: true,
                    defaultPrivacyLevel: `mask-user-input`,
                });
            } else {
                datadogRum.init({
                    applicationId: `e6d764e5-0667-4976-b10e-fb1421f83062`,
                    clientToken: `pube57e41c9279fde940a70ec1c0095ee07`,
                    site: `datadoghq.com`,
                    service: `cew`,
                    env: `${process.env.GATSBY_DD_RUM_ENV_CEW}`,
                    sessionSampleRate: 70,
                    sessionReplaySampleRate: 20,
                    trackUserInteractions: true,
                    trackResources: true,
                    trackLongTasks: true,
                    defaultPrivacyLevel: `mask-user-input`,
                });
            }
            datadogRum.startSessionReplayRecording();
        });
    }, [isPolicy]);

    useEffect(() => {
        initAutoAnalytics();
        return () => cleanupAutoAnalytics();
    }, []);

    const handleTabOnBlur = useCallback(
        (event) => {
            const currentTarget = event.currentTarget;
            requestAnimationFrame(() => {
                if (!currentTarget.contains(document.activeElement)) {
                    setSitesNavExpanded(false);
                }
            });
        },
        [onBlur]
    );

    const handleEscape = (event: React.KeyboardEvent) => {
        if (event.key === KeyboardKey.Escape) {
            setSitesNavExpanded(false);
            shopSpectrumServiceButtonRef?.current.focus();
        }
    };

    return (
        <>
            <header
                className={`site-header ${sitesNavExpanded ? `expanded` : ``}`}
                ref={sitesNavRef}
                onBlur={handleTabOnBlur}
                style={hideNav ? { visibility: `hidden` } : undefined}
                aria-hidden={hideNav}
            >
                <div className="kite-container services-header">
                    <button
                        id="shop-spectrum-services-btn"
                        type="button"
                        ref={shopSpectrumServiceButtonRef}
                        onClick={() => {
                            setSitesNavExpanded(!sitesNavExpanded);
                        }}
                        onKeyDown={handleEscape}
                        aria-label={`${
                            sitesNavExpanded ? `Collapse` : `Expand`
                        } Spectrum Sites navigation section`}
                        aria-expanded={sitesNavExpanded}
                        aria-controls="spectrum-service-nav"
                    >
                        Shop Spectrum Services
                    </button>
                </div>
                <nav
                    id="spectrum-service-nav"
                    className={`sites-nav ${
                        sitesNavExpanded ? `expanded` : ``
                    }`}
                    aria-hidden={!sitesNavExpanded}
                    onKeyDown={handleEscape}
                >
                    <SitesMenu />
                </nav>
            </header>
            <div
                className={`prevent-reflow ${
                    sitesNavExpanded ? `expanded` : ``
                }`}
            ></div>
            <header
                ref={headerRef}
                className={`nav-header ${isPolicy ? `policy ` : ``}${
                    searchInline ? `search-inline` : ``
                }`}
                style={hideNav ? { visibility: `hidden` } : undefined}
                aria-hidden={hideNav}
            >
                <FocusTrap
                    active={searchOpen && !searchInline}
                    focusTrapOptions={{
                        allowOutsideClick: true,
                        clickOutsideDeactivates: true,
                        initialFocus: `#inputSearch`,
                        fallbackFocus: `#inputSearch`,
                    }}
                >
                    <section>
                        <div className="kite-container">
                            {!isPolicy && (
                                <SidebarTrigger
                                    sidebarOpen={sidebarOpen}
                                    setSidebarOpen={setSidebarOpen}
                                    searchOpen={searchOpen}
                                />
                            )}
                            <a
                                href="https://corporate.charter.com"
                                rel="home"
                                aria-hidden={!isPolicy && searchOpen}
                                className={
                                    !isPolicy && searchOpen ? `hidden` : ``
                                }
                                onClick={() => {
                                    AnalyticsController.track({
                                        event: `global header click`,
                                        globalHeaderLink: `charter logo`,
                                    });
                                }}
                            >
                                <img
                                    src={logo}
                                    alt="Charter Communications logo"
                                />
                            </a>
                            {!(!isPolicy && searchOpen) && <DesktopMainMenu />}
                            {!isPolicy && !searchOpen && (
                                <SearchTrigger setSearchOpen={setSearchOpen} />
                            )}
                            {!isPolicy && searchOpen && (
                                <SearchInput
                                    searchOpen={searchOpen}
                                    setSearchOpen={setSearchOpen}
                                />
                            )}
                        </div>
                        {!isPolicy && searchOpen && (
                            <SearchResults setSearchOpen={setSearchOpen} />
                        )}
                    </section>
                </FocusTrap>
            </header>
            {isPolicy && (
                <PolicyHeader
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    searchOpen={searchOpen}
                    setSearchOpen={setSearchOpen}
                    searchInline={searchInline}
                    refHeaderHeight={headerHeight}
                />
            )}
            {showChild && <CompanyBanner pathname={pathname} />}
        </>
    );
};
